import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, INK, MOTION, NEUTRAL, PERSON_ROW, SURFACE } from "../../../theme/tokens";
import { AccentIconTile } from "../molecules/AccentIconTile";

/**
 * Row border + fill.
 *  - `neutral` recedes: one of several equivalent people.
 *  - `accent` marks the one the system is recommending (brand-tinted, so it re-tints with the theme).
 *  - `success` marks a row whose action is already done — settled, not inviting another click.
 */
export type PersonActionRowTone = "neutral" | "accent" | "success";

type Props = {
	/** The person's display name — the row's headline. */
	name: string;
	/** Secondary line under the name (tenure, weekly hours, employment type). Already translated. */
	meta?: ReactNode;
	/** Inline annotations beside the name — a `MetaChip` "Recommended", an OT flag. */
	badges?: ReactNode;
	/** Leading tile. Defaults to a muted person glyph; pass an avatar/initials tile to override. */
	avatar?: ReactNode;
	tone?: PersonActionRowTone;
	/**
	 * Overrides the colour the `accent` tone resolves to. Defaults to the org's brand primary; pass a
	 * fixed accent (e.g. `ACCENT_BLUE.MAIN`) on a surface whose design specifies one, so the row's tint
	 * matches the action button sitting in it.
	 */
	accentColor?: string;
	/** Trailing action — a `SystemButton`, or a `MetaChip` once the action is done. */
	action?: ReactNode;
	/** Position in the list; drives the entrance stagger only. Omit for no stagger. */
	index?: number;
	/** Tighter row for a person list nested inside a card that already carries a header. */
	dense?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * One person, what is true about them, and the one thing you can do about it — the row shape that had
 * been hand-rolled (a `PersonIcon` in a grey circle, a bold name, an 11px meta line, a `ButtonBase`
 * painted blue) in every staff picker in the app.
 *
 * Generic on purpose: it renders only what it is handed. The caller owns the data (a nurse, a
 * candidate, an applicant), the badges, and the action — including the action's *done* state, since
 * "already requested" is the caller's fact, not the row's. Tone drives a left state rail plus a faint
 * wash so a list reads at a glance (which one is recommended, which are already handled) without any
 * row shouting. All colour comes from the theme palette, so it re-tints with the org's accent.
 *
 * For a whole table of people reach for `SystemDataGrid`; this is the card/panel list shape.
 */
export const PersonActionRow: FC<Props> = ({ name, meta, badges, avatar, tone = "neutral", accentColor, action, index, dense, dataTestId, sx }) => {
	return (
		<Box
			data-testid={dataTestId}
			data-tone={tone}
			sx={[
				(theme) => {
					const accent = tone === "accent" ? (accentColor ?? theme.palette.primary.main) : tone === "success" ? theme.palette.success.main : null;
					return {
						position: "relative",
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1,
						minHeight: dense ? PERSON_ROW.MIN_HEIGHT_DENSE : PERSON_ROW.MIN_HEIGHT,
						px: dense ? 1.25 : 1.5,
						py: dense ? 0.75 : 1,
						borderRadius: 1.5,
						// A tinted border + faint fill (rather than a left rail): the row has to read as its
						// own bordered object inside a card, and the tint alone is what separates "the one
						// we recommend" from the equivalent people under it.
						border: `1px solid ${accent ? alpha(accent, 0.32) : NEUTRAL[200]}`,
						backgroundColor: accent ? alpha(accent, 0.06) : SURFACE,
						transition: theme.transitions.create(["background-color", "border-color"], {
							duration: MOTION.DURATION.CONTROL,
							easing: MOTION.EASE_OUT,
						}),
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { backgroundColor: accent ? alpha(accent, 0.1) : NEUTRAL[50] },
						},
						...(index === undefined
							? {}
							: {
									"@keyframes personRowEnter": {
										from: { opacity: 0, transform: "translateY(4px)" },
										to: { opacity: 1, transform: "translateY(0)" },
									},
									animation: `personRowEnter ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT} both`,
									animationDelay: `${Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX)}ms`,
								}),
						"@media (prefers-reduced-motion: reduce)": { animation: "none", transition: "none" },
					};
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{avatar ?? (
				<AccentIconTile size="xs" color={INK.MUTED} tint={NEUTRAL[100]}>
					<PersonOutlineRoundedIcon />
				</AccentIconTile>
			)}
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.125, minWidth: 0, flex: "1 1 auto" }}>
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
					<Typography
						component="span"
						sx={{
							fontSize: FONT_SIZE.EMPHASIS,
							fontWeight: 700,
							color: INK.PRIMARY,
							lineHeight: 1.25,
							minWidth: 0,
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{name}
					</Typography>
					{badges}
				</Box>
				{meta && (
					<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: NEUTRAL[500], lineHeight: 1.35 }}>
						{meta}
					</Typography>
				)}
			</Box>
			{action && <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto", flexShrink: 0 }}>{action}</Box>}
		</Box>
	);
};

export default PersonActionRow;
